import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Menu, MenuButton, Button, Portal, MenuList, MenuItem,
} from "@chakra-ui/react";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { useDeactivateUser } from "features/User/hooks";
import { User } from "features/User/models";
import { UpdateRolesModalButton } from "features/User/Components/UpdateRolesModalButton";
import { useQueryClient } from "@tanstack/react-query";
import {
  adminKeys, useDeleteAutomations, useDeleteUser, useImpersonateUser, useValidateAutomations,
} from "./hooks";

const CONFIRM_DELETE_USER = (name: string) => ({
  header: `Delete ${name}?`,
  body: `Are you sure you'd like to delete ${name}?`,
  confirmButtonText: "Yes, delete",
});

const CONFIRM_DELETE_AUTOMATIONS = (name: string) => ({
  header: `Remove Automations for ${name}?`,
  body: `Are you sure you'd like to delete all automations for ${name}?`,
  confirmButtonText: "Yes, delete",
});

export default function AdminMenuButton({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const { fullName } = user;
  const { deactivatedAt } = user;
  const { mutate: deactivate } = useDeactivateUser(user, () => {
    queryClient.invalidateQueries({ queryKey: adminKeys.users() });
  });
  const { mutate: impersonateUser } = useImpersonateUser(user);
  const { mutate: deleteUser } = useDeleteUser(user);
  const { mutate: deleteAutomations } = useDeleteAutomations(user);
  const { mutate: validateAutomations } = useValidateAutomations(user);

  return (
    <Menu>
      <MenuButton as={Button} size="xs" colorScheme="gray" rightIcon={<ChevronDownIcon />}>
        Actions
      </MenuButton>
      <Portal>
        <MenuList>
          <MenuItem onClick={() => impersonateUser()}>Impersonate</MenuItem>
          <MenuItem onClick={() => validateAutomations()}>Validate Automations</MenuItem>
          <MenuItem onClick={() => deactivate()}>{!deactivatedAt ? "Deactivate User" : "Reactivate User"}</MenuItem>
          <UpdateRolesModalButton user={user} isAdmin>
            <MenuItem>Update Roles</MenuItem>
          </UpdateRolesModalButton>
          <ButtonWithConfirm
            {...CONFIRM_DELETE_USER(fullName)}
            intent="warning"
            handleConfirm={() => deleteUser()}
          >
            <MenuItem>Delete User</MenuItem>
          </ButtonWithConfirm>
          <ButtonWithConfirm
            {...CONFIRM_DELETE_AUTOMATIONS(fullName)}
            intent="warning"
            handleConfirm={() => deleteAutomations()}
          >
            <MenuItem>Delete Automations</MenuItem>
          </ButtonWithConfirm>
        </MenuList>
      </Portal>
    </Menu>
  );
}
